import React, { useMemo } from "react";
import { InputNumber } from "antd";

export const DEFAULT_SEARCH_LIMITS = {
  min_tickets: 300,
  max_tickets: 300,
  max_big_winner: 150,
};

export const FILTERS_WITH_SEARCH_LIMITS = new Set([
  "analyzer",
  "no_big_prize_winners",
  "top_sellers",
  "medium_sellers",
  "low_sale_shops",
  "low_rtp_shops",
]);

export function defaultLimitsForFilter(filterType) {
  switch (filterType) {
    case "medium_sellers":
      return { min_tickets: 150, max_tickets: 300, max_big_winner: 150 };
    case "low_sale_shops":
      return { min_tickets: 0, max_tickets: 150, max_big_winner: 150 };
    case "no_big_prize_winners":
    case "low_rtp_shops":
      return { min_tickets: 300, max_tickets: 300, max_big_winner: 150 };
    case "top_sellers":
    case "analyzer":
    default:
      return { ...DEFAULT_SEARCH_LIMITS };
  }
}

function LimitHint({ children }) {
  return (
    <div
      style={{
        borderLeft: "4px solid #20c997",
        background: "#fff",
        padding: "12px 16px",
        marginBottom: 16,
        fontSize: 13,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

function LimitField({ label, help, prefix, suffix, value, onChange, min = 0 }) {
  return (
    <div className="col-md-4 col-lg-3">
      <label className="form-label small fw-semibold">{label}</label>
      <InputNumber
        min={min}
        value={value}
        onChange={(next) => onChange(next ?? 0)}
        style={{ width: "100%" }}
        addonBefore={prefix || undefined}
        addonAfter={suffix || undefined}
      />
      {help ? <div className="small text-muted mt-1">{help}</div> : null}
    </div>
  );
}

export default function BigPrizeSearchLimits({ filterType, limits, onChange }) {
  const { min_tickets, max_tickets, max_big_winner } = limits;

  const setLimit = (key, value) => {
    onChange({ ...limits, [key]: value ?? 0 });
  };

  const hint = useMemo(() => {
    switch (filterType) {
      case "analyzer":
        return (
          <>
            Top sellers: more than <strong style={{ color: "#c0392b" }}>{min_tickets}</strong>{" "}
            tickets, no win ≥ <strong style={{ color: "#c0392b" }}>{max_big_winner} QR</strong>.
            <br />
            Low RTP shops (payout below target).
          </>
        );
      case "no_big_prize_winners":
        return (
          <>
            Show shops that <strong style={{ color: "#c0392b" }}>never won a big prize</strong>.
            New prize must stay below{" "}
            <strong style={{ color: "#c0392b" }}>{max_big_winner} QR</strong>.
          </>
        );
      case "top_sellers":
        return (
          <>
            Show shops with more than{" "}
            <strong style={{ color: "#c0392b" }}>{min_tickets} tickets</strong> sold, and{" "}
            <strong style={{ color: "#c0392b" }}>no single win of {max_big_winner} QR or more</strong>{" "}
            in this period.
          </>
        );
      case "medium_sellers":
        return (
          <>
            Show shops with{" "}
            <strong style={{ color: "#c0392b" }}>
              {min_tickets} to {max_tickets} tickets
            </strong>{" "}
            sold, and{" "}
            <strong style={{ color: "#c0392b" }}>no single win of {max_big_winner} QR or more</strong>{" "}
            in this period.
          </>
        );
      case "low_sale_shops":
        return (
          <>
            Show shops with less than{" "}
            <strong style={{ color: "#c0392b" }}>{max_tickets} tickets</strong> sold, and{" "}
            <strong style={{ color: "#c0392b" }}>no single win of {max_big_winner} QR or more</strong>{" "}
            in this period.
          </>
        );
      case "low_rtp_shops":
        return (
          <>
            Show shops with payout below target, and{" "}
            <strong style={{ color: "#c0392b" }}>no single win of {max_big_winner} QR or more</strong>{" "}
            in this period.
          </>
        );
      default:
        return null;
    }
  }, [filterType, min_tickets, max_tickets, max_big_winner]);

  if (!FILTERS_WITH_SEARCH_LIMITS.has(filterType)) return null;

  return (
    <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #cbd5e1" }}>
      <div
        className="small fw-bold mb-3"
        style={{ color: "#20c997", letterSpacing: "0.04em" }}
      >
        SET YOUR SEARCH LIMITS
      </div>
      {hint ? <LimitHint>{hint}</LimitHint> : null}
      <div className="row g-3">
        {filterType === "analyzer" || filterType === "top_sellers" ? (
          <LimitField
            label={filterType === "analyzer" ? "Top — more than (tickets)" : "More than (tickets)"}
            prefix=">"
            value={min_tickets}
            onChange={(value) => setLimit("min_tickets", value)}
            help="Shops that sold more than this count"
          />
        ) : null}
        {filterType === "medium_sellers" ? (
          <>
            <LimitField
              label="From (tickets)"
              prefix=">"
              value={min_tickets}
              onChange={(value) => setLimit("min_tickets", value)}
              help="Shops that sold more than this count"
            />
            <LimitField
              label="To (tickets)"
              prefix="<"
              value={max_tickets}
              onChange={(value) => setLimit("max_tickets", value)}
              help="Shops that sold less than this count"
            />
          </>
        ) : null}
        {filterType === "low_sale_shops" ? (
          <LimitField
            label="Less than (tickets)"
            prefix="<"
            value={max_tickets}
            onChange={(value) => setLimit("max_tickets", value)}
            help="Shops that sold less than this count"
          />
        ) : null}
        <LimitField
          label="Max big winner"
          suffix="QR"
          value={max_big_winner}
          onChange={(value) => setLimit("max_big_winner", value)}
          help="Exclude shops that already won this amount or more"
        />
      </div>
    </div>
  );
}
